import { dayjsWithTimezone, parseJson } from '@healthhub/react-ui';

import type { JsonValue } from '@shared/services/appointment/questionnaire/types/questionnaireSaving';

import { checkHtmlContainsString, convertStringToByteCode, getContentInHTMLTag, removeHtmlInString } from './tools';
import type { QuestionTypeType } from '../../../shared/services/appointment/form/types/appointmentForm.type';
import { QuestionType } from '../../../shared/services/appointment/form/types/appointmentForm.type';
import type {
  FormAnswerQuestion,
  QuestionnaireItem,
  ReviewQuestionnaires,
} from '../../../shared/services/appointment/questionnaire/types/getQuestionnaire.type';
import { NUMBER_ORDER_QUESTIONNAIRE_REGEX } from '../constants';

export const getProgressQuestionnaire = (currentQuestionnaire: number, totalQuestionnaires: number): number =>
  Number(((currentQuestionnaire / totalQuestionnaires) * 100).toFixed(0)) || 0;

export const isCheckCanNextQuestionnaire = (values: FormAnswerQuestion) =>
  Object.keys(values).some((key) => values[key] !== undefined);

export const getSingHealthQuestionnaire = (questionnaireId: string) => {
  ///  hard code if questionnaireId !IMH
  if (!questionnaireId) return '';
  if (questionnaireId?.includes('IMH')) {
    return 'National Healthcare Group';
  }
  return 'SingHealth';
};

let globalQuestionIndex = 0;

export function getInitialAnswers(
  questionnaires: QuestionnaireItem[] = [],
  answers?: FormAnswerQuestion | null
): FormAnswerQuestion {
  const result: FormAnswerQuestion = {};

  function processQuestionnaire(questionnaire: QuestionnaireItem) {
    const { AdditionalItem, CurrentAnswer, DependencyQuestions, GroupItem, LinkId } = questionnaire;
    const answer = answers ? answers?.[LinkId] : CurrentAnswer;

    if (answer) {
      result[LinkId] = answer;
    }

    // Recursively process group items, if they exist
    DependencyQuestions?.forEach(processQuestionnaire);

    GroupItem?.forEach(processQuestionnaire);

    AdditionalItem?.forEach(processQuestionnaire);
  }

  questionnaires.forEach(processQuestionnaire);

  return result;
}

export function getQuestionnaireFilteredByAnswer({
  answers,
  isFirstGroupQuestion = true,
  questionnaire,
  tempIndex = 0,
}: {
  answers: FormAnswerQuestion | null;
  isFirstGroupQuestion?: boolean;
  questionnaire?: null | QuestionnaireItem[];
  tempIndex?: number;
}): QuestionnaireItem[] {
  if (!questionnaire) return [];
  globalQuestionIndex = tempIndex;

  const filterQuestionnaire = questionnaire.filter(
    (item) =>
      !answers?.[item?.EnableWhen?.Question ?? ''] ||
      !item.EnableWhen ||
      item.EnableWhen.Answer?.includes(answers?.[item.EnableWhen.Question] as string)
  );

  function processQuestion(
    questionList: QuestionnaireItem[],
    curQuestion: QuestionnaireItem,
    localIndex: number
  ): void {
    globalQuestionIndex = localIndex;
    function formatQuestionText(question: QuestionnaireItem): string {
      if (
        question.Type === QuestionType.Display ||
        question.LinkId === 'CGH033' ||
        !isFirstGroupQuestion ||
        curQuestion.IsRootGroup
      ) {
        return question.Text;
      }
      return `Q${localIndex}. ${question.Text}`;
    }

    questionList.push({
      ...curQuestion,
      GroupItem:
        curQuestion?.GroupItem && curQuestion.GroupItem?.length > 0
          ? getQuestionnaireFilteredByAnswer({
              answers,
              isFirstGroupQuestion: curQuestion.IsRootGroup,
              questionnaire: curQuestion.GroupItem,
              tempIndex: globalQuestionIndex,
            })
          : curQuestion.GroupItem,
      Text: formatQuestionText(curQuestion),
    });
  }

  const result = filterQuestionnaire.reduce((questionList: QuestionnaireItem[], curQuestion: QuestionnaireItem) => {
    if (curQuestion.Type !== QuestionType.Display && curQuestion.Type !== QuestionType.Group && isFirstGroupQuestion) {
      globalQuestionIndex += 1;
    }
    if (curQuestion.Type === QuestionType.Group && !curQuestion.IsRootGroup) {
      globalQuestionIndex += 1;
    }
    processQuestion(questionList, curQuestion, globalQuestionIndex);
    return questionList;
  }, []);

  return result;
}

function filterGroupItemsQuestionnaire(groupItems: QuestionnaireItem[], answerOption: FormAnswerQuestion) {
  if (!Array.isArray(groupItems)) {
    return null;
  }

  return groupItems
    .filter((group) => group.Type !== QuestionType.Display)
    .map((group) => ({
      ...group,
      Answer: answerOption[group.LinkId],
      ...(group.GroupItem && {
        GroupItem: filterGroupItemsQuestionnaire(group.GroupItem, answerOption),
      }),
      ...(group.DependencyQuestions && {
        DependencyQuestions: group.DependencyQuestions.map((dependencyQuestion) => ({
          ...dependencyQuestion,
          Answer: answerOption?.[dependencyQuestion.LinkId] ?? '',
        })),
      }),
    }));
}

export const filterQuestionnaireWithDependencies = (questionnaires?: QuestionnaireItem[]) => {
  if (!questionnaires) return [];
  const isDependencyQuestion = (question: QuestionnaireItem, currentLinkId: string): boolean =>
    question.EnableWhen?.Question === currentLinkId &&
    question.LinkId !== currentLinkId &&
    question.LinkId.includes('-');

  const result = questionnaires?.reduce((formatted: QuestionnaireItem[], current) => {
    const existingQuestion = formatted.some((item) =>
      item.DependencyQuestions?.some((question) => question.LinkId === current.LinkId)
    );

    if (!existingQuestion) {
      formatted.push({
        ...current,
        DependencyQuestions: questionnaires?.filter((question) => isDependencyQuestion(question, current.LinkId)),
        GroupItem: current?.GroupItem?.length
          ? (filterQuestionnaireWithDependencies(current?.GroupItem) ?? current?.GroupItem)
          : current?.GroupItem,
      });
    }
    return formatted;
  }, []);
  return result;
};

export function formatReviewQuestionnaire(
  listQuestionnairesFormatted: QuestionnaireItem[],
  answerOption: FormAnswerQuestion
) {
  let listReviewQuestionnaireFormatted: ReviewQuestionnaires[] = [];
  const formattedAnswerOptions = Object.entries(answerOption).map(([id, value]) => ({ id, value }));

  if (!formattedAnswerOptions.length || !listQuestionnairesFormatted.length) return [];

  const listQuestionnaireReviewFormatted = listQuestionnairesFormatted.reduce(
    (questions: (QuestionnaireItem | ReviewQuestionnaires)[], currentQuestion: QuestionnaireItem) => {
      // check if we don't have answer in listQuestionnaireReviewFormatted => remove on review
      if (
        answerOption[currentQuestion.LinkId] ||
        currentQuestion.Type === QuestionType.Group ||
        currentQuestion.Type === QuestionType.Display
      ) {
        if (currentQuestion.Type === QuestionType.Display) {
          questions.push({
            reviewQuestions: [],
            title: getContentInHTMLTag(currentQuestion.Text),
          } as ReviewQuestionnaires);
        } else {
          const lastQuestion = questions[questions.length - 1] as ReviewQuestionnaires;
          const hasReviewQuestions = lastQuestion?.reviewQuestions?.length > 0;
          if (hasReviewQuestions) {
            lastQuestion.reviewQuestions.push(currentQuestion);
          } else {
            questions.push({ ...currentQuestion });
          }
        }
      } else {
        questions.push({ ...currentQuestion });
      }
      return questions;
    },
    []
  );

  const groupedQuestionnaireReview: (QuestionnaireItem | ReviewQuestionnaires)[] = [];
  let currentGroup: null | ReviewQuestionnaires = null;

  listQuestionnaireReviewFormatted.forEach((questionnaireReview) => {
    if ('title' in questionnaireReview) {
      currentGroup = {
        reviewQuestions: [],
        title: questionnaireReview.title,
      };
      groupedQuestionnaireReview.push(currentGroup);
    } else if (currentGroup) {
      currentGroup.reviewQuestions.push(questionnaireReview);
    } else {
      groupedQuestionnaireReview?.push(questionnaireReview);
    }
  });

  // check the questionnaire has no title property (no type display)

  if (!groupedQuestionnaireReview.some((item) => Object.hasOwn(item, 'title'))) {
    listReviewQuestionnaireFormatted = [
      {
        reviewQuestions: (groupedQuestionnaireReview as QuestionnaireItem[]).map((question) => ({
          ...question,
          Answer: answerOption?.[question.LinkId] || '',
          ...(question.DependencyQuestions && {
            DependencyQuestions: question.DependencyQuestions.map((dependencyQuestion) => ({
              ...dependencyQuestion,
              Answer: answerOption?.[dependencyQuestion.LinkId] ?? '',
            })),
          }),
          ...(question.GroupItem && {
            GroupItem: filterGroupItemsQuestionnaire(question.GroupItem, answerOption),
          }),
        })),
        title: 'Summary',
      },
    ];
  } else {
    listReviewQuestionnaireFormatted = (groupedQuestionnaireReview as ReviewQuestionnaires[])?.map((question) => ({
      ...question,
      reviewQuestions: question.reviewQuestions.map((reviewQuestion) => ({
        ...reviewQuestion,
        Answer: answerOption?.[reviewQuestion.LinkId] || '',
        ...(reviewQuestion.DependencyQuestions && {
          DependencyQuestions: reviewQuestion.DependencyQuestions.map((dependencyQuestion) => ({
            ...dependencyQuestion,
            Answer: answerOption?.[dependencyQuestion.LinkId] ?? '',
          })),
        }),
        ...(reviewQuestion.GroupItem && {
          GroupItem: filterGroupItemsQuestionnaire(reviewQuestion.GroupItem, answerOption),
        }),
      })),
    }));
  }

  const listReviewQuestionnaireFilterAnswer = listReviewQuestionnaireFormatted?.map((list) => ({
    ...list,
    reviewQuestions: list?.reviewQuestions?.filter(
      (question) => question.LinkId || question.Type === QuestionType.Group // check KKH
    ),
  }));

  return listReviewQuestionnaireFilterAnswer;
}

export const formatPayloadSubmitQuestionnaire = (
  linkId: string,
  answer: string,
  questionnaireFilteredByAnswer: QuestionnaireItem[]
) => {
  function extractGroupLinkIds(data: null | QuestionnaireItem[]) {
    const results: QuestionnaireItem[] = [];
    if (data) {
      data.forEach((item) => {
        if (item.GroupItem) {
          results.push(...extractGroupLinkIds(item.GroupItem));
        }
        if (item.LinkId) {
          results.push(item);
        }
      });
    }
    return results;
  }

  const findQuestionnaireType = (linkId: string) => {
    const question = questionnaireFilteredByAnswer?.find((questionnaire) => questionnaire.LinkId === linkId);

    const dependencyQuestions = questionnaireFilteredByAnswer?.flatMap(
      (questionnaire) => questionnaire?.DependencyQuestions ?? []
    );
    const dependentQuestion = dependencyQuestions?.find((questionnaire) => questionnaire.LinkId === linkId);

    if (dependentQuestion?.Type) {
      if (dependentQuestion?.Type === QuestionType.Choice && dependentQuestion.Repeats) {
        return QuestionType.CheckBox;
      }
      if (dependentQuestion?.Type === QuestionType.Choice && !!dependentQuestion?.IsDropDown) {
        return QuestionType.Dropdown;
      }
      return dependentQuestion?.Type;
    }
    const groupQuestions = extractGroupLinkIds(questionnaireFilteredByAnswer);
    const groupQuestion = groupQuestions?.find((questionnaire) => questionnaire.LinkId === linkId);

    if (groupQuestion) {
      if (groupQuestion?.Type === QuestionType.Choice && groupQuestion?.Repeats) {
        return QuestionType.CheckBox;
      }
      if (groupQuestion?.Type === QuestionType.Choice && !!groupQuestion?.IsDropDown) {
        return QuestionType.Dropdown;
      }
      return groupQuestion?.Type;
    }

    if (question?.Type === QuestionType.Choice && question.Repeats) {
      return QuestionType.CheckBox;
    }
    if (question?.Type === QuestionType.Choice && !!question?.IsDropDown) {
      return QuestionType.Dropdown;
    }
    return question?.Type;
  };

  function createPayload<T extends 'valueBoolean' | 'valueCoding' | 'valueDate' | 'valueInteger' | 'valueString'>(
    key: T,
    value: T extends 'valueBoolean' ? boolean : T extends 'valueCoding' ? Record<'code', string> : string
  ) {
    return { [key]: value };
  }

  const convertedToByteAnswer = convertStringToByteCode(answer);

  switch (findQuestionnaireType(linkId)) {
    case QuestionType.Boolean:
      return [createPayload('valueBoolean', answer !== 'false')];
    case QuestionType.Choice:
    case QuestionType.Dropdown:
      return [createPayload('valueCoding', { code: convertedToByteAnswer })];
    case QuestionType.Date:
      return [createPayload('valueDate', dayjsWithTimezone(answer).format('YYYY-MM-DD'))];
    case QuestionType.Integer:
      return [createPayload('valueInteger', convertedToByteAnswer)];
    case QuestionType.String:
    case QuestionType.Text:
      return [createPayload('valueString', convertedToByteAnswer)];
    case QuestionType.CheckBox:
      try {
        const answers = JSON.parse(answer);
        return answers.map((i) => createPayload('valueCoding', { code: i }));
      } catch (error) {
        console.warn('Error parsing checkbox answer:', error);
        if (Array.isArray(answer)) {
          return answer.map((i) => createPayload('valueCoding', { code: i }));
        }
        return [createPayload('valueCoding', { code: answer })];
      }
    default:
      return [createPayload('valueCoding', { code: convertedToByteAnswer })];
  }
};

export function getQuestionnaireJsonValue({
  questionRecords,
  values,
}: {
  questionRecords?: Record<string, string>;
  values: FormAnswerQuestion;
}): JsonValue[] {
  const getTitle = (id: string) => {
    if (!questionRecords) return undefined;
    if (NUMBER_ORDER_QUESTIONNAIRE_REGEX.test(questionRecords[id])) {
      return questionRecords[id].split(`${questionRecords[id].split('. ')[0]}. `)[1];
    }
    return id.includes('_') ? questionRecords[id.split('_')[0]] : questionRecords[id];
  };

  const questionnaireJsonValue: JsonValue[] = Object.entries(values)
    .filter(([, value]) => !!value || value === 0)
    .map(([key, value]) => ({
      IsHtml: checkHtmlContainsString(String(values[key])),
      LinkId: key,
      Title: getTitle(key),
      Value: typeof values[key] === 'string' ? convertStringToByteCode(value as string) : value,
    }));
  return questionnaireJsonValue;
}

function formatQuestionnaireAnswer(content: string) {
  switch (content) {
    case 'false':
      return 'No';
    case 'true':
      return 'Yes';
    default:
      return removeHtmlInString(content);
  }
}

export const getReviewAnswerByQuestionType = ({ answer, type }: { answer: string; type: QuestionTypeType }): string => {
  if (type === QuestionType.Date) {
    return dayjsWithTimezone(answer).format('DD MMM YYYY');
  }
  if (type === QuestionType.CheckBox) {
    try {
      const parsed = parseJson(answer);
      return Array.isArray(parsed) ? parsed.join(', ') : answer.split(',').join(', ');
    } catch (error) {
      console.warn('Error parsing getReviewAnswerByQuestionType:', error);
      return answer.split(',').join(', ');
    }
  }

  return formatQuestionnaireAnswer(answer);
};

export const getQuestionnaireExceptionType = ({
  IsDropDown,
  Repeats,
  type,
}: {
  IsDropDown?: boolean | null;
  Repeats?: boolean | null;
  type: QuestionTypeType;
}) => {
  if (type === QuestionType.Choice && Repeats) {
    return QuestionType.CheckBox;
  }
  if (type === QuestionType.Choice && IsDropDown) {
    return QuestionType.Dropdown;
  }
  return type;
};

export const getQuestionText = ({ questionText }: { questionText?: string }) => {
  if (!questionText) return '';

  return checkHtmlContainsString(questionText) ? removeHtmlInString(questionText) : questionText;
};

export const trimAnswerValues = (answers: FormAnswerQuestion) => {
  Object.keys(answers).forEach((key) => {
    if (typeof answers[key] === 'string') {
      answers[key] = String(answers[key]).trim();
    }
  });
};
